import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Box, Group, Loader, Paper, Text } from '@mantine/core';
import { IoClose, IoImageOutline, IoSend } from 'react-icons/io5';
import { Button, Textarea } from '@shared/ui';
import {
  clientSupportChatApi,
  CLIENT_SUPPORT_CHAT_MAX_IMAGES,
} from '@modules/client-support-chat/api/client-support-chat.api';
import {
  appendClientSupportMessageToCache,
  chatMessagesQueryKey,
  CHAT_THREADS_QUERY_KEY,
  CHAT_UNREAD_QUERY_KEY,
  mergeClientSupportMessages,
} from '@modules/client-support-chat/chat-cache';
import { useClientSupportChatSocket } from '@modules/client-support-chat/hooks/useClientSupportChatSocket';
import { ChatBubbleImages } from '@modules/client-support-chat/components/ChatBubbleImages';
import type { ClientSupportMessage, ClientSupportMessagesResponse } from '@modules/client-support-chat/types';
import {
  isResolvedOrderProblem,
  isSellerInvitedToOrderProblem,
  isSystemSupportMessage,
} from '@modules/client-support-chat/types';
import { devProxyS3Url } from '@shared/utils/dev-proxy-s3-url';
import './ClientSupportChatPanel.css';

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function chatDayKey(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function chatTime(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function chatDayLabel(iso: string) {
  const key = chatDayKey(iso);
  const today = chatDayKey(new Date().toISOString());
  const yesterday = chatDayKey(new Date(Date.now() - 86_400_000).toISOString());
  if (key === today) return 'Сегодня';
  if (key === yesterday) return 'Вчера';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return key;
  const withYear = d.getFullYear() !== new Date().getFullYear();
  return withYear
    ? `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`
    : `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`;
}

function messageKey(message: ClientSupportMessage) {
  return `${message.id}:${message.createdAt}`;
}

function userInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
}

type PendingImage = {
  id: string;
  file: File;
  previewUrl: string;
};

type ClientSupportChatPanelProps = {
  userId: number;
  userName?: string | null;
  userPhone?: string | null;
  compact?: boolean;
};

export function ClientSupportChatPanel({
  userId,
  userName,
  userPhone,
  compact = false,
}: ClientSupportChatPanelProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const messagesContentRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const ignoreScrollUntilRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const queryKey = chatMessagesQueryKey(userId);

  const messagesQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const data = await clientSupportChatApi.getMessages(userId, { limit: 100 });
      const prev = queryClient.getQueryData<ClientSupportMessagesResponse>(queryKey);
      return mergeClientSupportMessages(prev, data);
    },
    enabled: userId > 0,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const markReadMutation = useMutation({
    mutationFn: () => clientSupportChatApi.markRead(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHAT_THREADS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CHAT_UNREAD_QUERY_KEY });
    },
  });

  const clearPendingImages = useCallback(() => {
    setPendingImages((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
  }, []);

  const sendMutation = useMutation({
    mutationFn: ({ body, images }: { body: string; images: File[] }) =>
      clientSupportChatApi.sendMessage(userId, body, images),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
    },
    onSuccess: (message) => {
      setDraft('');
      clearPendingImages();
      stickToBottomRef.current = true;
      appendClientSupportMessageToCache(queryClient, userId, message);
      queryClient.invalidateQueries({ queryKey: CHAT_THREADS_QUERY_KEY });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (messageId: number) =>
      clientSupportChatApi.resolveOrderProblem(userId, messageId),
    onSuccess: (message) => {
      appendClientSupportMessageToCache(queryClient, userId, message);
      queryClient.invalidateQueries({ queryKey: CHAT_THREADS_QUERY_KEY });
    },
  });

  const inviteSellerMutation = useMutation({
    mutationFn: (messageId: number) =>
      clientSupportChatApi.inviteSeller(userId, messageId),
    onSuccess: (message) => {
      appendClientSupportMessageToCache(queryClient, userId, message);
      // Обновляем invitedSellerId на плашке ORDER_PROBLEM
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: CHAT_THREADS_QUERY_KEY });
    },
  });

  const appendMessage = useCallback(
    (message: ClientSupportMessage, messageUserId: number) => {
      if (messageUserId !== userId) return;
      appendClientSupportMessageToCache(queryClient, userId, message);
      if (
        message.senderKind === 'CLIENT' ||
        message.senderKind === 'SELLER' ||
        message.systemEvent === 'ORDER_PROBLEM'
      ) {
        markReadMutation.mutate();
      }
    },
    [markReadMutation, queryClient, userId],
  );

  useClientSupportChatSocket({
    onMessage: ({ userId: messageUserId, message }) =>
      appendMessage(message, messageUserId),
    onUnread: ({ scope, count, userId: unreadUserId }) => {
      if (scope !== 'admin') return;
      queryClient.invalidateQueries({ queryKey: CHAT_THREADS_QUERY_KEY });
      if (unreadUserId === userId) return;
      queryClient.setQueryData(CHAT_UNREAD_QUERY_KEY, { count });
    },
  });

  useEffect(() => {
    if (!messagesQuery.isSuccess) return;
    markReadMutation.mutate();
  }, [messagesQuery.isSuccess, userId]);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const messagesSignature =
    messagesQuery.data?.items?.map((m) => m.id).join(',') ?? '';

  const prevUserIdRef = useRef<number | null>(null);
  if (prevUserIdRef.current !== userId) {
    prevUserIdRef.current = userId;
    stickToBottomRef.current = true;
    ignoreScrollUntilRef.current = Date.now() + 900;
  }

  useLayoutEffect(() => {
    if (!stickToBottomRef.current) return;
    scrollToBottom();
    const id = requestAnimationFrame(() => {
      scrollToBottom();
      requestAnimationFrame(scrollToBottom);
    });
    return () => cancelAnimationFrame(id);
  }, [
    messagesSignature,
    messagesQuery.isSuccess,
    messagesQuery.isFetching,
    userId,
    sendMutation.isSuccess,
    pendingImages.length,
    scrollToBottom,
  ]);

  useEffect(() => {
    if (!stickToBottomRef.current) return undefined;
    const timers = [50, 150, 350, 700].map((ms) =>
      window.setTimeout(() => {
        if (stickToBottomRef.current) scrollToBottom();
      }, ms),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [userId, messagesSignature, messagesQuery.isSuccess, scrollToBottom]);

  useEffect(() => {
    const content = messagesContentRef.current;
    if (!content) return undefined;
    const ro = new ResizeObserver(() => {
      if (stickToBottomRef.current) scrollToBottom();
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [userId, messagesQuery.isSuccess, messagesSignature, scrollToBottom]);

  const handleMessagesScroll = () => {
    if (Date.now() < ignoreScrollUntilRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distance < 120;
  };

  useEffect(() => {
    clearPendingImages();
    setDraft('');
  }, [userId, clearPendingImages]);

  useEffect(() => {
    return () => {
      pendingImages.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revoke on unmount only
  }, []);

  const messages = messagesQuery.data?.items ?? [];
  const sortedMessages = useMemo(
    () => [...messages].sort((a, b) => a.id - b.id),
    [messages],
  );

  const handlePickImages = (files: FileList | null) => {
    if (!files?.length) return;
    const next: PendingImage[] = [];
    const room = CLIENT_SUPPORT_CHAT_MAX_IMAGES - pendingImages.length;
    Array.from(files)
      .filter((file) => file.type.startsWith('image/'))
      .slice(0, Math.max(room, 0))
      .forEach((file) => {
        next.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
          file,
          previewUrl: URL.createObjectURL(file),
        });
      });
    if (next.length === 0) return;
    setPendingImages((prev) => [...prev, ...next].slice(0, CLIENT_SUPPORT_CHAT_MAX_IMAGES));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingImage = (id: string) => {
    setPendingImages((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  };

  const handleSend = () => {
    const body = draft.trim();
    const images = pendingImages.map((item) => item.file);
    if ((!body && images.length === 0) || sendMutation.isPending) return;
    sendMutation.mutate({ body, images });
  };

  const canSend = Boolean(draft.trim() || pendingImages.length > 0);
  const headerTitle = userName?.trim() || `Клиент #${userId}`;

  const stickyOrderProblems = useMemo(
    () =>
      sortedMessages.filter(
        (message) =>
          message.systemEvent === 'ORDER_PROBLEM' &&
          !isResolvedOrderProblem(sortedMessages, message.id),
      ),
    [sortedMessages],
  );
  const stickyOrderProblemIds = useMemo(
    () => new Set(stickyOrderProblems.map((message) => message.id)),
    [stickyOrderProblems],
  );

  return (
    <Paper withBorder radius="md" className={`client-support-chat-panel ${compact ? 'client-support-chat-panel--compact' : ''}`}>
      <div className="client-support-chat-panel__header">
        <Group wrap="nowrap" gap="sm">
          <Avatar radius={8} size={40} color="gray">
            {userInitials(headerTitle)}
          </Avatar>
          <div className="client-support-chat-panel__header-text">
            <Text fw={600}>{headerTitle}</Text>
            <Text size="xs" c="dimmed">
              {userPhone?.trim() || 'Чат с клиентом'}
            </Text>
          </div>
        </Group>
      </div>
      {stickyOrderProblems.length > 0 ? (
        <div className="client-support-chat-panel__sticky-problems">
          {stickyOrderProblems.map((problem) => {
            const canInvite = !isSellerInvitedToOrderProblem(
              sortedMessages,
              problem.id,
            );
            return (
              <div
                key={problem.id}
                className="client-support-chat-panel__sticky-problem"
              >
                {problem.orderId ? (
                  <button
                    type="button"
                    className="client-support-chat-panel__sticky-problem-title client-support-chat-panel__sticky-problem-title--link"
                    onClick={() =>
                      navigate(`/orders/${problem.orderId}`, {
                        state: { from: 'client-support-chat', userId },
                      })
                    }
                  >
                    {problem.body}
                  </button>
                ) : (
                  <Text
                    size="sm"
                    fw={600}
                    className="client-support-chat-panel__sticky-problem-title"
                  >
                    {problem.body}
                  </Text>
                )}
                <div className="client-support-chat-panel__system-actions">
                  {canInvite ? (
                    <button
                      type="button"
                      className="client-support-chat-panel__system-invite"
                      disabled={inviteSellerMutation.isPending}
                      onClick={() => inviteSellerMutation.mutate(problem.id)}
                    >
                      Подключить продавца
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="client-support-chat-panel__system-resolve"
                    disabled={resolveMutation.isPending}
                    onClick={() => resolveMutation.mutate(problem.id)}
                  >
                    Проблема решена
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
      <Box
        ref={scrollRef}
        className="client-support-chat-panel__messages"
        onScroll={handleMessagesScroll}
      >
        <div
          ref={messagesContentRef}
          className="client-support-chat-panel__messages-inner"
        >
        {messagesQuery.isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : null}
        {!messagesQuery.isLoading && sortedMessages.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            Сообщений пока нет. Напишите клиенту первым.
          </Text>
        ) : null}
        {sortedMessages.map((message, index) => {
          const prev = sortedMessages[index - 1];
          const showDay =
            !prev || chatDayKey(prev.createdAt) !== chatDayKey(message.createdAt);
          const dayChip = showDay ? (
            <div className="client-support-chat-panel__day">
              <span>{chatDayLabel(message.createdAt)}</span>
            </div>
          ) : null;
          if (isSystemSupportMessage(message)) {
            const isOpenProblem =
              message.systemEvent === 'ORDER_PROBLEM' &&
              !isResolvedOrderProblem(sortedMessages, message.id);
            const isStickyProblem = stickyOrderProblemIds.has(message.id);
            const sellerAlreadyInvited =
              message.systemEvent === 'ORDER_PROBLEM' &&
              isSellerInvitedToOrderProblem(sortedMessages, message.id);
            const invitedName =
              message.invitedSellerName ||
              sortedMessages.find(
                (m) =>
                  m.systemEvent === 'SELLER_INVITED' &&
                  m.relatedMessageId === message.id,
              )?.invitedSellerName ||
              null;
            // Активная проблема — кнопки на плавающей панели сверху
            if (isStickyProblem) {
              return (
                <div key={messageKey(message)} className="client-support-chat-panel__item">
                  {dayChip}
                  <div className="client-support-chat-panel__system">
                  <div className="client-support-chat-panel__system-pill">
                    <Text size="xs" fw={500}>
                      {message.body}
                    </Text>
                    {sellerAlreadyInvited ? (
                      <Text size="xs" c="dimmed">
                        Продавец подключён{invitedName ? `: ${invitedName}` : ''}
                      </Text>
                    ) : null}
                  </div>
                  </div>
                </div>
              );
            }
            const canInviteSeller =
              isOpenProblem && !sellerAlreadyInvited;
            return (
              <div key={messageKey(message)} className="client-support-chat-panel__item">
                {dayChip}
                <div className="client-support-chat-panel__system">
                <div className="client-support-chat-panel__system-pill">
                  <Text size="xs" fw={500}>
                    {message.body}
                  </Text>
                  {sellerAlreadyInvited ? (
                    <Text size="xs" c="dimmed">
                      Продавец подключён{invitedName ? `: ${invitedName}` : ''}
                    </Text>
                  ) : null}
                  {isOpenProblem ? (
                    <div className="client-support-chat-panel__system-actions">
                      {canInviteSeller ? (
                        <button
                          type="button"
                          className="client-support-chat-panel__system-invite"
                          disabled={inviteSellerMutation.isPending}
                          onClick={() => inviteSellerMutation.mutate(message.id)}
                        >
                          Подключить продавца
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="client-support-chat-panel__system-resolve"
                        disabled={resolveMutation.isPending}
                        onClick={() => resolveMutation.mutate(message.id)}
                      >
                        Проблема решена
                      </button>
                    </div>
                  ) : null}
                </div>
                </div>
              </div>
            );
          }
          const mine = message.senderKind === 'SUPER_ADMIN';
          const fromSeller = message.senderKind === 'SELLER';
          const images = (message.imageUrls ?? [])
            .map((url) => devProxyS3Url(url) || url)
            .filter(Boolean);
          const label = mine
            ? 'Вы'
            : fromSeller
              ? message.senderName || 'Продавец'
              : message.senderName || 'Клиент';
          const stacked = Boolean(
            prev &&
            !showDay &&
            !isSystemSupportMessage(prev) &&
            prev.senderKind === message.senderKind,
          );
          return (
            <div key={messageKey(message)} className="client-support-chat-panel__item">
              {dayChip}
              <div
                className={[
                  'client-support-chat-panel__bubble',
                  mine
                    ? 'client-support-chat-panel__bubble--mine'
                    : fromSeller
                      ? 'client-support-chat-panel__bubble--seller'
                      : 'client-support-chat-panel__bubble--theirs',
                  stacked ? 'client-support-chat-panel__bubble--stacked' : '',
                  images.length > 0 ? 'client-support-chat-panel__bubble--has-images' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {stacked ? null : (
                  <div className="client-support-chat-panel__bubble-author">{label}</div>
                )}
                {images.length > 0 ? <ChatBubbleImages urls={images} /> : null}
                <div className="client-support-chat-panel__bubble-body">
                  {message.body ? (
                    <div className="client-support-chat-panel__bubble-text">{message.body}</div>
                  ) : null}
                  <time
                    className="client-support-chat-panel__bubble-time"
                    dateTime={message.createdAt}
                  >
                    {chatTime(message.createdAt)}
                  </time>
                </div>
              </div>
            </div>
          );
        })}
          <div className="client-support-chat-panel__messages-end" aria-hidden />
        </div>
      </Box>
      <Box className="client-support-chat-panel__composer">
        {pendingImages.length > 0 ? (
          <div className="client-support-chat-panel__composer-previews">
            {pendingImages.map((item) => (
              <div key={item.id} className="client-support-chat-panel__composer-preview">
                <img src={item.previewUrl} alt="" />
                <button
                  type="button"
                  className="client-support-chat-panel__composer-preview-remove"
                  onClick={() => removePendingImage(item.id)}
                  aria-label="Убрать фото"
                >
                  <IoClose />
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <div className="client-support-chat-panel__composer-row">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(event) => handlePickImages(event.currentTarget.files)}
          />
          <Button
            type="button"
            variant="secondary"
            className="client-support-chat-panel__composer-attach"
            onClick={() => fileInputRef.current?.click()}
            disabled={
              sendMutation.isPending || pendingImages.length >= CLIENT_SUPPORT_CHAT_MAX_IMAGES
            }
            aria-label="Прикрепить фото"
          >
            <IoImageOutline />
          </Button>
          <Textarea
            className="client-support-chat-panel__composer-input"
            placeholder="Сообщение клиенту…"
            minRows={1}
            maxRows={6}
            autosize
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSend();
              }
            }}
          />
          <Button
            className="client-support-chat-panel__composer-send"
            onClick={handleSend}
            loading={sendMutation.isPending}
            disabled={!canSend}
            aria-label="Отправить"
          >
            <IoSend />
          </Button>
        </div>
      </Box>
    </Paper>
  );
}
